import React, { useEffect, useState } from 'react';
import { Clock3, UserX, DoorClosed, AlertTriangle, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { easerPost, errorMessage } from '../../lib/easerApi';
import { enqueue, shouldQueue, queuedBecause } from '../../lib/offlineQueue';

const KINDS = [
  { id: 'late', label: 'Je suis en retard', Icon: Clock3 },
  { id: 'absent', label: 'Client absent', Icon: UserX },
  { id: 'access', label: "Impossible d'accéder", Icon: DoorClosed },
  { id: 'other', label: 'Autre imprévu', Icon: AlertTriangle },
];
const DELAYS = [10, 15, 30, 45, 60];

/** Quick incident report: warns the agency by e-mail and leaves a trace on the Dolibarr event. */
export function IncidentDialog({ open, ev, onClose, onDone, onUnauthorized }) {
  const [kind, setKind] = useState('late');
  const [delay, setDelay] = useState(15);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setKind('late'); setDelay(15); setNote(''); } }, [open]);

  const submit = async () => {
    if (kind === 'other' && !note.trim()) return toast.error('Décrivez l\'imprévu en quelques mots');
    setSaving(true);
    try {
      const res = await easerPost(`/api/easer/events/${ev.id}/incident`, { kind, delay_min: kind === 'late' ? delay : null, note });
      toast.success(`Imprévu signalé à l'agence : ${res.data.kind_label}`);
      onDone();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      if (shouldQueue(err)) {
        await enqueue({ type: 'incident', eventId: ev.id, label: ev.label, payload: { kind, delay_min: kind === 'late' ? delay : null, note } });
        toast.info(queuedBecause(err, 'imprévu'));
        return onDone();
      }
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md w-[95vw] rounded-2xl" data-testid="incident-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-['Space_Grotesk']">Signaler un imprévu</DialogTitle>
          <DialogDescription>{ev?.label}{ev?.client?.name ? ` · ${ev.client.name}` : ''} — l'agence est prévenue immédiatement par e-mail.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Type d'imprévu">
            {KINDS.map(({ id, label, Icon }) => (
              <button key={id} type="button" role="radio" aria-checked={kind === id} onClick={() => setKind(id)} data-testid={`incident-kind-${id}`}
                className={`h-16 rounded-xl border text-sm font-medium flex flex-col items-center justify-center gap-1 transition-colors ${kind === id ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]' : 'border-border text-foreground hover:bg-[hsl(var(--secondary))]'}`}>
                <Icon className="h-5 w-5" /> {label}
              </button>
            ))}
          </div>

          {kind === 'late' && (
            <div className="space-y-1.5">
              <Label>Retard estimé</Label>
              <div className="flex flex-wrap gap-2">
                {DELAYS.map((d) => (
                  <button key={d} type="button" onClick={() => setDelay(d)} data-testid={`incident-delay-${d}`}
                    className={`h-10 px-3 rounded-lg border text-sm font-medium transition-colors ${delay === d ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-border hover:bg-[hsl(var(--secondary))]'}`}>~{d} min</button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="incident-note">Précision {kind === 'other' ? '' : '(optionnel)'}</Label>
            <Textarea id="incident-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={kind === 'absent' ? 'Sonné 2 fois, pas de réponse, message laissé...' : 'Quelques mots pour l\'agence'} data-testid="incident-note" />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="incident-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="incident-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Prévenir l'agence
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
